/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Sergey Mysov
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */
import { Fragment, useEffect, useRef, useState } from 'react';

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { ELinkType, IInstructionLinkProps } from '@components/common/InstructionLink/types';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import { Button, InstructionLink, LinkBlock } from '../../../../../../components/common/index';
import { IOptionItemProps } from '../../../../../../components/common/Select/Select';
import { IPageSubscriberMobileTranslation } from '../../../../../../modules/internationalization/types/index';
import { Loader } from '../../../../../../modules/loader/components/Loader';
import { useTableFilterApi } from '../../../../../../modules/table';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import {
    deleteExtFilter,
    loadExtFilter,
    saveExtFilter,
    sendExtFilter,
    updateExtFilter,
} from '../../actions/extFilterApiActions';
import { setExtFilter, setExtFilterId } from '../../actions/extFilterConstructorActions';
import {
    makeSelectExtFilter,
    makeSelectExtFilterId,
    makeSelectExtFilterItems,
    makeSelectExtFilterList,
    makeSelectExtFilterLoaded,
    makeSelectExtFilterMeta,
    makeSelectExtFilterTabIndex,
} from '../../extFilterSelectors';
import { ISaveExtFilterPayload, ISendExtFilterPayload } from '../../types/extFilterActionTypes';
import { IExtFilterData, IExtFilterItem, IExtFilterItemValue, SAVED_FILTERS } from '../../types/extFilterTypes';
import ExtFilterConstructorBlock from '../ExtFilterConstructorBlock/ExtFilterConstructorBlock';
import { scrollToView } from '../ExtFilterConstructorBlock/ExtFilterConstructorBlockUitls';
import ExtFilterControlBlock from '../ExtFilterControlBlock/ExtFilterControlBlock';
import {
    ExtFilterFooterCloseStyled,
    ExtFilterFooterContainerStyled,
    ExtFilterHeaderStyled,
    ExtFilterPopupStyled,
    TitleDivStyled,
} from '../styled/SubscriberMobileExtFilterStyled';


/**
 * @description Ext filter props
 * @interface IConnectedProps
 * @property {boolean} dataLoaded - data loading flag
 * @property {Function} setExtFilterId - set filter id
 * @property {Function} setExtFilterItems - set new filter item options
 */
interface IConnectedProps extends IExtFilterData {
    dataLoaded: boolean;
    extFilterId: string;
    extFilterItems: IExtFilterItem[];
    tabIndex?: number;
}

/**
 * @description Ext filter props
 * @interface IDispatchProps
 * @property {Function} deleteExtFilterSaga - delete external filter data
 * @property {Function} loadExtFilterSaga - load external filter data
 * @property {Function} saveExtFilterSaga - save external filter data
 * @property {Function} sendExtFilterSaga - send external filter data
 * @property {Function} setExtFilterId - set filter id
 * @property {Function} setExtFilterItems - set new filter item options
 * @property {Function} updateExtFilterSaga - update saved filter data
 */

interface IDispatchProps {
    deleteExtFilter: (payload: string) => void;
    loadExtFilter: () => void;
    saveExtFilter: (payload: ISaveExtFilterPayload) => void;
    sendExtFilter: (payload: ISendExtFilterPayload) => void;
    setExtFilterId: (payload: string) => void;
    setExtFilterItems: (payload: IExtFilterItemValue) => void;
    updateExtFilter: (payload: ISendExtFilterPayload) => void;
}

export interface ISubscriberMobileExtFilterProps {
    closeExtFilter: () => void;
    translation: IPageSubscriberMobileTranslation['extFilter'];
}

export type ExtFilterProps = ISubscriberMobileExtFilterProps & IConnectedProps & IDispatchProps;

export const ExtFilterPopup = ({
    closeExtFilter,
    dataLoaded,
    deleteExtFilter,
    extFilterId,
    extFilterItems,
    extFilterList,
    extFilterMeta,
    loadExtFilter,
    saveExtFilter,
    sendExtFilter,
    setExtFilterId,
    setExtFilterItems,
    tabIndex,
    translation,
    updateExtFilter,
}: ExtFilterProps) => {

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const isInstructionsLinksEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.INSTRUCTIONS_LINKS));
    const { instructionsLinks: { subsFilter } } = translationCommon;
    const instructionLinkProps: IInstructionLinkProps = { ...subsFilter, linkType: ELinkType.TEXT };

    const [highlightEmpty, setHighlightEmpty] = useState(false);
    const [isUpdateFilters, setIsUpdateFilters] = useState(false);

    const filterRef = useRef<HTMLDivElement>(null);

    const { drop: dropTableFilter } = useTableFilterApi();

    const applyExtFilter = (): void => {
        if (checkEmpty()) {
            setHighlightEmpty(true);
        } else {
            closeExtFilter();
            const request: ISendExtFilterPayload = {
                items: extFilterItems,
                dropCommonTableFilter: dropTableFilter,
            };
            if (extFilterId) {
                request.id = extFilterId;
            }
            sendExtFilter(request);
        }
    };

    const selectExtFilterHandler = (id: IOptionItemProps): void => {
        const value = id.value !== SAVED_FILTERS ? id.value : '';
        setExtFilterId(value.toString());
        setIsUpdateFilters(false);
    };

    const onChangeHandler = (payload: IExtFilterItemValue) => {
        setHighlightEmpty(false);
        extFilterId ? setIsUpdateFilters(true) : setIsUpdateFilters(false);
        setExtFilterItems(payload);
    };

    const onSaveHandler = (type: string, filter: string) => {
        if (checkEmpty()) {
            setHighlightEmpty(true);
            return;
        }
        switch (type) {
            case 'create':
                saveExtFilter({
                    items: extFilterItems,
                    name: filter,
                });
                setIsUpdateFilters(false);
                break;
            case 'save':
                updateExtFilter({
                    id: extFilterId,
                    items: extFilterItems,
                    dropCommonTableFilter: dropTableFilter,
                });
                setIsUpdateFilters(false);
                break;
            case 'delete':
                deleteExtFilter(extFilterId);
                setIsUpdateFilters(false);
                break;
            default:
                break;
        }
    };

    const checkEmpty = (): boolean => {
        return (
            extFilterItems.length === 0 ||
            extFilterItems.some((item: IExtFilterItem) => !item.attr || !item.operator || !item.value)
        );
    };


    useEffect(() => {
        loadExtFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // @ts-ignore
        scrollToView(tabIndex, filterRef);
    }, [dataLoaded, extFilterItems.length, tabIndex]);

    return (
        <ExtFilterPopupStyled data-component={'modal-popup'} data-name={'ext-filter-popup'} ref={filterRef}>
            <TitleDivStyled>
                <ExtFilterHeaderStyled>{translation.title}</ExtFilterHeaderStyled>
                {isInstructionsLinksEnabled &&
                    <InstructionLink {...instructionLinkProps} />
                }
            </TitleDivStyled>
            {dataLoaded ? (
                <Fragment>
                    <ExtFilterControlBlock
                        extFilterId={extFilterId}
                        extFilterList={extFilterList}
                        isUpdateFilters={isUpdateFilters}
                        translation={translation}
                        onChange={selectExtFilterHandler}
                        onSave={onSaveHandler}
                    />
                    <ExtFilterConstructorBlock
                        extFilterMeta={extFilterMeta}
                        highlightEmpty={highlightEmpty}
                        items={extFilterItems}
                        translation={translation}
                        onChange={onChangeHandler}
                    />
                    <ExtFilterFooterContainerStyled>
                        <Button dataName="applyExtendedFilter" kind="mediumNormal" onClick={applyExtFilter}>
                            {translation.apply}
                        </Button>
                        <ExtFilterFooterCloseStyled onClick={closeExtFilter}>
                            <LinkBlock dataName="closeExtendedFilter">{translation.close}</LinkBlock>
                        </ExtFilterFooterCloseStyled>
                    </ExtFilterFooterContainerStyled>
                </Fragment>
            ) : (
                <Loader showLoader={true} />
            )}
        </ExtFilterPopupStyled>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, ISubscriberMobileExtFilterProps, IConnectedProps>({
    dataLoaded: makeSelectExtFilterLoaded(),
    extFilter: makeSelectExtFilter(),
    extFilterId: makeSelectExtFilterId(),
    extFilterItems: makeSelectExtFilterItems(),
    extFilterList: makeSelectExtFilterList(),
    extFilterMeta: makeSelectExtFilterMeta(),
    tabIndex: makeSelectExtFilterTabIndex(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    deleteExtFilter: (payload: string) => dispatch(deleteExtFilter(payload)),
    loadExtFilter: () => dispatch(loadExtFilter()),
    saveExtFilter: (payload: ISaveExtFilterPayload) => dispatch(saveExtFilter(payload)),
    sendExtFilter: (payload: ISendExtFilterPayload) => dispatch(sendExtFilter(payload)),
    setExtFilterId: (payload: string) => dispatch(setExtFilterId(payload)),
    setExtFilterItems: (payload: IExtFilterItemValue) => dispatch(setExtFilter(payload)),
    updateExtFilter: (payload: ISendExtFilterPayload) => dispatch(updateExtFilter(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExtFilterPopup);
